import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getCmiEmpresas } from "../../helpers/configApi";
import { getFinanzasV2Cierres } from "../../helpers/finanzasV2Api";

const FALLBACK_EMPRESAS = [
    { id: 'all', label: 'Todas las empresas' },
    { id: 13, label: 'Visados Empresas' },
    { id: 12, label: 'Permiso de Trabajo' },
    { id: 14, label: 'CVS' },
];

const STORAGE_KEY = 'cmi_empresa_id';
const VISTA_KEY = 'cmi_vista';
const VISTAS = ['anual', 'mensual'];
const ESCENARIO_KEY = 'cmi_escenario';
const ESCENARIOS = ['proyeccion_lineal', 'presupuesto_mensual'];

const loadEmpresa = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === null) return 'all';
        return JSON.parse(stored);
    } catch {
        return 'all';
    }
};

const loadVista = () => {
    try {
        const stored = localStorage.getItem(VISTA_KEY);
        return VISTAS.includes(stored) ? stored : 'anual';
    } catch {
        return 'anual';
    }
};

const loadEscenario = () => {
    try {
        const stored = localStorage.getItem(ESCENARIO_KEY);
        return ESCENARIOS.includes(stored) ? stored : 'proyeccion_lineal';
    } catch {
        return 'proyeccion_lineal';
    }
};

const _resolveErpIds = (state) => {
    const { empresaId, empresasList } = state.Filters;
    if (!empresaId || empresaId === 'all') return 'all';
    const found = empresasList.find(e => e.id === empresaId);
    if (found?.empresas_erp_ids) return found.empresas_erp_ids.join(',');
    if (typeof empresaId === 'number') return String(empresaId);
    return String(empresaId);
};

export const fetchEmpresas = createAsyncThunk('filters/fetchEmpresas', async () => {
    const data = await getCmiEmpresas();
    return data;
});

export const fetchCierres = createAsyncThunk('filters/fetchCierres', async (_, { getState }) => {
    const erpIds = _resolveErpIds(getState());
    const { year } = getState().Filters;
    return await getFinanzasV2Cierres(erpIds, year);
});

const initialState = {
    empresaId: loadEmpresa(),
    year: new Date().getFullYear(),
    vista: loadVista(),
    escenario: loadEscenario(),
    mes: null,
    cierres: [],
    cierresLoading: false,
    empresasList: FALLBACK_EMPRESAS,
    empresasLoaded: false,
};

const filtersSlice = createSlice({
    name: 'Filters',
    initialState,
    reducers: {
        setEmpresa: (state, action) => {
            state.empresaId = action.payload;
            try { localStorage.setItem(STORAGE_KEY, JSON.stringify(action.payload)); } catch {}
        },
        setYear: (state, action) => {
            state.year = parseInt(action.payload);
        },
        setVista: (state, action) => {
            state.vista = action.payload;
            try { localStorage.setItem(VISTA_KEY, action.payload); } catch {}
        },
        setEscenario: (state, action) => {
            state.escenario = action.payload;
            try { localStorage.setItem(ESCENARIO_KEY, action.payload); } catch {}
        },
        setMes: (state, action) => {
            state.mes = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchCierres.pending, (state) => {
            state.cierresLoading = true;
        });
        builder.addCase(fetchCierres.fulfilled, (state, action) => {
            state.cierres = action.payload || [];
            state.cierresLoading = false;
            const cerrados = state.cierres.filter(c => c.estado === 'cerrado').map(c => c.mes);
            if (cerrados.length > 0 && state.mes == null) {
                state.mes = Math.max(...cerrados);
            }
        });
        builder.addCase(fetchCierres.rejected, (state) => {
            state.cierresLoading = false;
        });
        builder.addCase(fetchEmpresas.fulfilled, (state, action) => {
            const cmiEmpresas = action.payload;
            if (cmiEmpresas && cmiEmpresas.length > 0) {
                const list = [{ id: 'all', label: 'Todas las empresas' }];
                for (const emp of cmiEmpresas) {
                    list.push({
                        id: emp.empresas_erp_ids.length === 1 ? emp.empresas_erp_ids[0] : `cmi_${emp.id}`,
                        cmiEmpresaId: emp.id,
                        label: emp.nombre,
                        empresas_erp_ids: emp.empresas_erp_ids,
                    });
                }
                state.empresasList = list;
            }
            state.empresasLoaded = true;
        });
        builder.addCase(fetchEmpresas.rejected, (state) => {
            state.empresasLoaded = true;
        });
    },
});

export const { setEmpresa, setYear, setVista, setEscenario, setMes } = filtersSlice.actions;

export const resolveErpIds = _resolveErpIds;

export default filtersSlice.reducer;
